import { useState } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/Card'
import { cn } from '@/lib/utils'
import type { Plan } from '@/services/api/billing/useGetPlans'
import type { BillingInterval } from '@/services/api/billing/useGetBillingSubscription'
import { annualSavingPercent, formatMoney, INTERVAL_LABEL } from './format'
import EarlyAccessButton from './EarlyAccessButton'

interface PlanPickerProps {
  plans: Plan[]
  /** Highlights the plan + interval the space is on (change-plan mode). */
  current?: { planKey: string | null; interval: BillingInterval | null }
  actionLabel: string
  busy?: boolean
  onChoose: (planKey: string, interval: BillingInterval) => void
  /** Early access request source for plans that are not on sale yet. */
  earlyAccessSource: 'plan_page' | 'billing_page'
}

export default function PlanPicker({
  plans,
  current,
  actionLabel,
  busy = false,
  onChoose,
  earlyAccessSource,
}: PlanPickerProps) {
  const [interval, setInterval] = useState<BillingInterval>(
    current?.interval ?? 'month',
  )
  const [pending, setPending] = useState<string | null>(null)

  const bestSaving = Math.max(
    0,
    ...plans.map(
      (p) =>
        annualSavingPercent(p.prices.month?.amount, p.prices.year?.amount) ?? 0,
    ),
  )

  const choose = (planKey: string) => {
    setPending(planKey)
    onChoose(planKey, interval)
  }

  return (
    <div className="space-y-4">
      <div
        role="radiogroup"
        aria-label="Billing interval"
        className="inline-flex rounded-md border border-primary p-0.5 text-sm"
      >
        {(['month', 'year'] as BillingInterval[]).map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={interval === value}
            onClick={() => setInterval(value)}
            className={cn(
              'rounded px-3 py-1 font-medium transition-colors',
              interval === value
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {value === 'month' ? 'Monthly' : 'Annual'}
            {value === 'year' && bestSaving > 0 && (
              <span className="ml-1.5 text-xs font-normal opacity-80">
                save up to {bestSaving}%
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {plans.map((plan) => {
          const price = plan.prices[interval]
          const isCurrent =
            current?.planKey === plan.plan_key && current?.interval === interval
          const saving =
            interval === 'year'
              ? annualSavingPercent(
                  plan.prices.month?.amount,
                  plan.prices.year?.amount,
                )
              : null

          return (
            <Card
              key={plan.plan_key}
              className={cn(
                'flex w-full min-w-0 flex-col gap-3 p-6',
                isCurrent && 'border-primary ring-1 ring-primary',
              )}
            >
              <div>
                <p className="text-base font-semibold">{plan.name}</p>
                {plan.description && (
                  <p className="mt-1 text-sm text-muted-foreground">
                    {plan.description}
                  </p>
                )}
              </div>

              {!plan.purchasable ? (
                // Not on sale yet: teased, never priced or bought.
                <p className="w-fit rounded bg-primary/10 px-2 py-1 text-sm font-medium text-primary">
                  In development — coming soon
                </p>
              ) : price ? (
                <div>
                  <p className="text-xl font-semibold">
                    {formatMoney(price.amount, price.currency)}
                    <span className="text-sm font-normal text-muted-foreground">
                      {' '}
                      / {INTERVAL_LABEL[interval]}
                    </span>
                  </p>
                  {saving && (
                    <p className="text-xs text-green-700">
                      {formatMoney(price.amount / 12, price.currency)} / month —
                      save {saving}%
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Not available {interval === 'year' ? 'annually' : 'monthly'}
                </p>
              )}

              {plan.inclusions.length > 0 && (
                <ul className="space-y-1.5 text-sm">
                  {plan.inclusions.map((inclusion) => (
                    <li key={inclusion} className="flex items-start gap-2">
                      <Check className="mt-0.5 size-4 shrink-0 text-green-600" />
                      <span className="min-w-0">{inclusion}</span>
                    </li>
                  ))}
                </ul>
              )}

              {isCurrent ? (
                <Button className="mt-auto" variant="outline" disabled>
                  <Check className="mr-2 size-4" />
                  Current plan
                </Button>
              ) : !plan.purchasable ? (
                <EarlyAccessButton
                  className="mt-auto"
                  source={earlyAccessSource}
                />
              ) : (
                <Button
                  className="mt-auto"
                  onClick={() => choose(plan.plan_key)}
                  disabled={busy || !price}
                >
                  {busy && pending === plan.plan_key
                    ? 'Redirecting…'
                    : actionLabel}
                </Button>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}
